import { ModuleTypeEnum } from './module'
import {
  createFullModulePermission,
  createRegularUserPermission,
  PermissionActionEnum,
  PermissionDefinition,
} from './permissions'

/**
 * Default Module-level Permissions - Simplified 2-tier system
 * Only creates permissions for modules shown in the main navigation (plus hidden/core internal modules)
 */
export const PERMISSION_MODULE_TYPES: ModuleTypeEnum[] = [
  ModuleTypeEnum.TODAY,
  ModuleTypeEnum.TASK,
  ModuleTypeEnum.TICKET,
  ModuleTypeEnum.TIME_ENTRY,
  ModuleTypeEnum.THREAD,
  ModuleTypeEnum.CALENDAR,
  ModuleTypeEnum.EVENT,
  ModuleTypeEnum.PROJECT,
  ModuleTypeEnum.ENTITY,
  ModuleTypeEnum.NOTE,
  ModuleTypeEnum.DEAL,
  ModuleTypeEnum.CONTACT,
  ModuleTypeEnum.CUSTOM_COLLECTION,
  ModuleTypeEnum.TEAM,
  ModuleTypeEnum.INVENTORY,
  ModuleTypeEnum.SUPPLIER,
  ModuleTypeEnum.CONTRACT,
  ModuleTypeEnum.INVOICE,
  ModuleTypeEnum.BILLING_AUTHORIZATION,
  ModuleTypeEnum.EXPENSE,
  ModuleTypeEnum.FORM,
  ModuleTypeEnum.WORKFLOW,
  ModuleTypeEnum.ANALYTICS,
  ModuleTypeEnum.AI,
  ModuleTypeEnum.WORKSTREAM,
  ModuleTypeEnum.ORGANIZATION,
  ModuleTypeEnum.INTEGRATION,
]

export const HIDDEN_PERMISSION_MODULE_TYPES: ModuleTypeEnum[] = [
  ModuleTypeEnum.CUSTOM_FIELD,
]

export const DEFAULT_PERMISSION_MODULE_TYPES: ModuleTypeEnum[] = [
  ...PERMISSION_MODULE_TYPES,
  ...HIDDEN_PERMISSION_MODULE_TYPES,
  ModuleTypeEnum.MEMBER,
  ModuleTypeEnum.DOCUMENT,
]

export const MODULE_PERMISSIONS: Partial<
  Record<ModuleTypeEnum, PermissionDefinition[]>
> = DEFAULT_PERMISSION_MODULE_TYPES.reduce(
  (acc, moduleType) => {
    acc[moduleType] = [
      createFullModulePermission(moduleType),
      createRegularUserPermission(moduleType),
    ].filter(Boolean)
    return acc
  },
  {} as Partial<Record<ModuleTypeEnum, PermissionDefinition[]>>,
)

if (MODULE_PERMISSIONS[ModuleTypeEnum.TIME_ENTRY]) {
  MODULE_PERMISSIONS[ModuleTypeEnum.TIME_ENTRY]!.push({
    id: 'timesheets_approve',
    name: 'Timesheets - Approve',
    description: 'Approve or reject time entries',
    moduleType: ModuleTypeEnum.TIME_ENTRY,
    allowedActions: [PermissionActionEnum.APPROVE],
    isSystem: true,
    isActive: true,
    isLimited: true,
  })
}

// Watching a project subscribes the user to category-level notifications for
// all activity in it. Gated on PROJECT + APPROVE (unused by project CRUD) so
// it is grantable independently of read/manage access. Opt-in: seeded into no
// permission set by default; org owners/admins bypass permission checks.
if (MODULE_PERMISSIONS[ModuleTypeEnum.PROJECT]) {
  MODULE_PERMISSIONS[ModuleTypeEnum.PROJECT]!.push({
    id: 'projects_watch',
    name: 'Projects - Watch',
    description: 'Watch projects to receive notifications for all activity',
    moduleType: ModuleTypeEnum.PROJECT,
    allowedActions: [PermissionActionEnum.APPROVE],
    isSystem: true,
    isActive: true,
    isLimited: true,
  })
}

// Read-only billing-authorization definition. createRegularUserPermission
// returns null for BILLING_AUTHORIZATION, so without this the only BA grant
// would be Full Access (MANAGE). This preserves read-only access for users who
// previously held INVOICE read (BA endpoints used to require the INVOICE perm).
const READ_ONLY_DEFINITIONS: {
  moduleType: ModuleTypeEnum
  id: string
  name: string
}[] = [
  {
    moduleType: ModuleTypeEnum.BILLING_AUTHORIZATION,
    id: 'billing_authorization_read_only',
    name: 'Billing Authorizations - Read Only',
  },
]

for (const def of READ_ONLY_DEFINITIONS) {
  if (MODULE_PERMISSIONS[def.moduleType]) {
    MODULE_PERMISSIONS[def.moduleType]!.push({
      id: def.id,
      name: def.name,
      description: `Read-only access to ${def.name.replace(' - Read Only', '').toLowerCase()}`,
      moduleType: def.moduleType,
      allowedActions: [PermissionActionEnum.READ],
      isSystem: true,
      isActive: true,
      isLimited: true,
    })
  }
}

// Helper to safely get permission ID, ensuring it's defined
const getPermissionId = (permission: PermissionDefinition): string => {
  if (!permission.id) {
    throw new Error(`Permission ${permission.name} is missing an ID`)
  }
  return permission.id
}

// Helper to safely filter permissions by name pattern and return names (not IDs)
const filterPermissionsByName = (
  permissions: PermissionDefinition[],
  namePattern: string,
): string[] => {
  return permissions
    .filter(p => p.name && p.name.includes(namePattern))
    .map(p => p.name)
}

/**
 * Default Permission Sets - Simplified with better names
 */
export const DEFAULT_PERMISSION_SETS = [
  {
    id: 'team_member',
    name: 'Team Member',
    description:
      'Perfect for regular team members who work on projects, tasks, track time, and collaborate on core work.',
    systemKey: 'team_member',
    isSystem: true,
    isActive: true,
    permissions: [
      // Standard Access permissions for core modules - use the actual names from the utility functions
      'Projects - Standard Access',
      'Tasks - Standard Access',
      'Tickets - Standard Access',
      'Entities - Standard Access',
      'Timesheets - Standard Access',
      'Discussions - Standard Access',
      'Collections - Standard Access',
      'Notes - Standard Access',
      'Contacts - Standard Access',
      'Suppliers - Standard Access',
      'Calendar - Standard Access',
      'Members - Standard Access',
      'Documents - Standard Access',
      'Expenses - Standard Access',
      'Forms - Standard Access',
      'AI - Standard Access',
      'Workstreams - Standard Access',
      'My Organization - Standard Access',
      'Integrations - Standard Access',
    ],
  },
  {
    id: 'accountant',
    name: 'Accountant',
    description:
      'Accounting and financial management access. Can manage deals, contacts, contracts, and view financial reports.',
    systemKey: 'accountant',
    isSystem: true,
    isActive: true,
    permissions: [
      // Core business modules for accounting
      'Deals - Full Access',
      'Contacts - Full Access',
      'Contracts - Full Access',
      'Expenses - Full Access',
      // Team and Timesheets access for billing and time management
      'Team - Full Access',
      'Timesheets - Full Access',
      // Accountants can manage project cycle work without org admin membership.
      'Projects - Full Access',
      'Tasks - Full Access',
      'Tickets - Standard Access',
      'Entities - Standard Access',
      'Discussions - Standard Access',
      'Collections - Standard Access',
      'Notes - Standard Access',
      'Suppliers - Standard Access',
      'Calendar - Standard Access',
      'Members - Standard Access',
      'Documents - Standard Access',
      'Forms - Standard Access',
      'AI - Standard Access',
      'Workstreams - Standard Access',
      'My Organization - Standard Access',
    ],
  },
  {
    id: 'system_admin',
    name: 'System Administrator',
    description:
      'Ultimate system access with full permissions across all modules. Reserved for system administrators.',
    systemKey: 'system_admin',
    isSystem: true,
    isActive: true,
    permissions: [
      // Add all Full Access permissions for each module
      ...Object.values(MODULE_PERMISSIONS).flatMap(permissions =>
        filterPermissionsByName(permissions ?? [], 'Full Access'),
      ),
    ],
  },
]

/**
 * Personal (family) permission sets.
 *
 * Personal-account organizations seed this trio INSTEAD of the business trio
 * (Team Member / Accountant / System Administrator). The permission DEFINITIONS
 * (the 61 module-level grants) are identical for both account types — only the
 * SETS differ. Built from the exact same catalog (MODULE_PERMISSIONS / the
 * business sets) so the two account types stay in lockstep as the catalog grows.
 *
 *  - Family Admin  → equivalent to System Administrator (all Full Access).
 *  - Family Member → equivalent to Team Member (standard collaborative access).
 *  - Limited Member → a trimmed, kid-friendly preset. Standard Access on the
 *    shared personal modules (projects, tasks, notes, calendar, documents,
 *    contacts, vendors/suppliers, collections, events) plus
 *    read-only Members visibility, and NOTHING else — no My Organization
 *    settings grant, no AI/integration/timesheet/discussion grants, and none of
 *    the business modules. The catalog only exposes a "Standard Access" tier as
 *    its lowest per-module grant (there is no per-module read-only definition),
 *    and Standard Access is already record-scoped (`isLimited: true`, "manage
 *    content they created"), so "sees shared plans, manages their own tasks" is
 *    expressed as Standard Access on the shared modules minus every admin power.
 */
const _teamMemberSet = DEFAULT_PERMISSION_SETS.find(
  set => set.id === 'team_member',
)!
const _systemAdminSet = DEFAULT_PERMISSION_SETS.find(
  set => set.id === 'system_admin',
)!

export const PERSONAL_PERMISSION_SETS = [
  {
    id: 'family_admin',
    name: 'Family Admin',
    description: 'Full control of this space, including settings and invites.',
    systemKey: 'family_admin',
    isSystem: true,
    isActive: true,
    // Equivalent to system_admin: every Full Access grant in the catalog.
    permissions: [..._systemAdminSet.permissions],
  },
  {
    id: 'family_member',
    name: 'Family Member',
    description: 'Can plan, edit, and check things off across the space.',
    systemKey: 'family_member',
    isSystem: true,
    isActive: true,
    // Equivalent to team_member: standard collaborative access.
    permissions: [..._teamMemberSet.permissions],
  },
  {
    id: 'limited_member',
    name: 'Limited Member',
    description:
      'A simplified preset, great for kids — sees shared plans and manages their own tasks.',
    systemKey: 'limited_member',
    isSystem: true,
    isActive: true,
    permissions: [
      'Projects - Standard Access',
      'Tasks - Standard Access',
      'Notes - Standard Access',
      'Calendar - Standard Access',
      'Documents - Standard Access',
      'Contacts - Standard Access',
      'Suppliers - Standard Access',
      'Collections - Standard Access',
      'Events - Standard Access',
      // Members - Standard Access is READ-only (no member management / invites).
      'Members - Standard Access',
    ],
  },
]

/**
 * The system permission-set definitions to seed for a given account type.
 * PERSONAL seeds the family trio; everything else (ORGANIZATION / undefined)
 * seeds the business trio. Kept type-loose (string) so callers can pass the
 * Prisma/DTO account-type value without importing the enum here.
 */
export function getPermissionSetsForAccountType(
  accountType?: string | null,
): typeof DEFAULT_PERMISSION_SETS {
  return accountType === 'PERSONAL'
    ? (PERSONAL_PERMISSION_SETS as typeof DEFAULT_PERMISSION_SETS)
    : DEFAULT_PERMISSION_SETS
}

/**
 * Helper functions for permissions
 */

/**
 * Get all default permissions (both system and module level)
 */
export function getAllDefaultPermissions(): PermissionDefinition[] {
  return Object.values(MODULE_PERMISSIONS).flatMap(
    permissions => permissions ?? [],
  )
}

/**
 * Get default permissions for specific modules
 */
export function getModulePermissions(
  moduleTypes: ModuleTypeEnum[],
): PermissionDefinition[] {
  return moduleTypes.flatMap(moduleType => MODULE_PERMISSIONS[moduleType] || [])
}

/**
 * Get full list of permissions for a permission set by ID
 */
export function getPermissionSetPermissions(permissionSetId: string): string[] {
  const permissionSet = DEFAULT_PERMISSION_SETS.find(
    set => set.id === permissionSetId,
  )
  return permissionSet?.permissions || []
}

/**
 * Debug function to get the actual permissions in a permission set
 * This helps verify that permissions are being included correctly
 */
export function debugPermissionSet(permissionSetId: string): {
  permissionSet?: any
  permissions: { id: string; name: string; moduleType: string }[]
} {
  const permissionSet = DEFAULT_PERMISSION_SETS.find(
    set => set.id === permissionSetId,
  )
  if (!permissionSet) {
    return { permissions: [] }
  }

  const allPermissions = getAllDefaultPermissions()
  const resolvedPermissions = permissionSet.permissions
    .map(permId => allPermissions.find(p => p.id === permId))
    .filter(Boolean)
    .map(p => ({ id: p!.id!, name: p!.name, moduleType: p!.moduleType }))

  return {
    permissionSet,
    permissions: resolvedPermissions,
  }
}

/**
 * Debug function to check what entity permissions exist
 */
export function debugEntityPermissions(): {
  id: string
  name: string
  moduleType: string
}[] {
  return (MODULE_PERMISSIONS[ModuleTypeEnum.ENTITY] ?? []).map(p => ({
    id: p.id!,
    name: p.name,
    moduleType: p.moduleType,
  }))
}
